/* Version: 3.5.155 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React from 'react';
import PropTypes from 'prop-types';
import { DIRECTION, ALIGNMENT } from '../constants.js';
import Errors from '../Errors/Errors.js';
import Legend from '../Legend/Legend.js';
import RadioButton from '../RadioButton/RadioButton.js';
import Stack from '../Stack/Stack.js';
import { BREAKPOINTS } from '../../spacing/index.js';
import { safeRest, hasErrors, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { Root, Box, Fieldset, FlexBox, Label, Note } from './styledComponents.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useTheme from '../../hooks/useTheme.js';
import useMedia from '../../hooks/useMedia.js';
import componentWithName from '../../prop-types/componentWithName.js';

var _excluded = ["id", "name", "legend", "values", "selected", "defaultSelected", "controlledSelected", "alignment", "direction", "mobileDirection", "ariaRequired", "ariaLabel", "disabled", "errors", "errorCallout", "onChange", "tooltip", "customStyle", "gap", "gapX", "gapY", "wrap"];
var RadioGroup = function RadioGroup(_ref) {
  var controlledId = _ref.id,
    controlledName = _ref.name,
    _ref$legend = _ref.legend,
    legend = _ref$legend === undefined ? '' : _ref$legend,
    _ref$values = _ref.values,
    values = _ref$values === undefined ? [] : _ref$values,
    _ref$selected = _ref.selected,
    oldSelected = _ref$selected === undefined ? '' : _ref$selected,
    _ref$defaultSelected = _ref.defaultSelected,
    defaultSelected = _ref$defaultSelected === undefined ? '' : _ref$defaultSelected,
    controlledSelected = _ref.controlledSelected,
    alignment = _ref.alignment,
    _ref$direction = _ref.direction,
    direction = _ref$direction === undefined ? DIRECTION.ROW : _ref$direction,
    _ref$mobileDirection = _ref.mobileDirection,
    mobileDirection = _ref$mobileDirection === undefined ? DIRECTION.COLUMN : _ref$mobileDirection,
    _ref$ariaRequired = _ref.ariaRequired,
    ariaRequired = _ref$ariaRequired === undefined ? false : _ref$ariaRequired,
    ariaLabel = _ref.ariaLabel,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === undefined ? false : _ref$disabled,
    _ref$errors = _ref.errors,
    errors = _ref$errors === undefined ? [] : _ref$errors,
    _ref$errorCallout = _ref.errorCallout,
    errorCallout = _ref$errorCallout === undefined ? false : _ref$errorCallout,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === undefined ? noop : _ref$onChange,
    tooltip = _ref.tooltip,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      rootStyle: {},
      legendStyle: {},
      radioStyle: {},
      labelStyle: {},
      noteStyle: {}
    } : _ref$customStyle,
    gap = _ref.gap,
    gapX = _ref.gapX,
    _ref$gapY = _ref.gapY,
    gapY = _ref$gapY === undefined ? gap ? undefined : '10px' : _ref$gapY,
    _ref$wrap = _ref.wrap,
    wrap = _ref$wrap === undefined ? 'wrap' : _ref$wrap,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useDerivedState = useDerivedState(useId('radio-id-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var _useDerivedState3 = useDerivedState(useId('radio-name-'), controlledName),
    _useDerivedState4 = _slicedToArray(_useDerivedState3, 1),
    name = _useDerivedState4[0];
  // oldSelected and defaultSelected are synonymous here until a major release when they will be
  // collapsed into one component. controlledSelected allows a consuming app to override the
  // default behaviour if necessary.
  var _useDerivedState5 = useDerivedState(oldSelected || defaultSelected, controlledSelected),
    _useDerivedState6 = _slicedToArray(_useDerivedState5, 2),
    selected = _useDerivedState6[0],
    setSelected = _useDerivedState6[1];
  var theme = useTheme();
  var safeProps = safeRest(rest);
  var _safeCustomStyle = safeCustomStyle(customStyle),
    labelStyle = _safeCustomStyle.labelStyle,
    noteStyle = _safeCustomStyle.noteStyle,
    rootStyle = _safeCustomStyle.rootStyle;
  var hasError = hasErrors(errors);
  var errorsId = hasError ? "".concat(id, "-errors") : undefined;
  var derivedDirection = useMedia(["(max-width: ".concat(BREAKPOINTS.PHONE_PORTRAIT_LG, "px)")], [mobileDirection], direction);
  var isColumn = derivedDirection && derivedDirection === DIRECTION.COLUMN;
  var align = isColumn && alignment ? alignment : ALIGNMENT.START; // default horizontal alignment

  return /*#__PURE__*/jsx(Root, {
    rootStyle: rootStyle,
    children: /*#__PURE__*/jsx(Box, {
      children: /*#__PURE__*/jsxs(Fieldset, {
        "aria-describedby": errorsId,
        "aria-label": ariaLabel,
        "aria-required": ariaRequired,
        id: id,
        role: "radiogroup",
        children: [/*#__PURE__*/jsx(Legend, {
          id: "".concat(id, "-legend"),
          customStyle: {
            legendStyle: customStyle.legendStyle
          },
          tooltip: tooltip,
          children: legend
        }), /*#__PURE__*/jsx(FlexBox, {
          "data-testid": "radio-group-test",
          className: "radio-group",
          direction: direction,
          align: isColumn ? align : undefined,
          justify: isColumn ? undefined : alignment,
          gap: gap,
          gapX: gapX,
          gapY: gapY,
          wrap: wrap,
          mobileDirection: mobileDirection,
          children: values.map(function (e) {
            var label = e.label,
              value = e.value,
              note = e.note;
            var isChecked = selected === value;
            var buttonId = "".concat(id, "-").concat(value);
            return /*#__PURE__*/jsx(React.Fragment, {
              children: /*#__PURE__*/jsxs(Stack, {
                align: "flex-start",
                direction: "row",
                gap: "0",
                "data-testid": "item-group-".concat(value),
                style: {
                  flex: "".concat(!isColumn && alignment ? '0' : '1', " 0 auto")
                },
                wrap: "nowrap",
                children: [/*#__PURE__*/jsx(RadioButton, _objectSpread(_objectSpread({}, safeProps), {}, {
                  id: buttonId,
                  name: name,
                  type: "radio",
                  value: value,
                  checked: isChecked,
                  onChange: disabled ? noop : function (_checked, event) {
                    setSelected(value);
                    handle(onChange)(event, value);
                  },
                  disabled: disabled,
                  customStyle: {
                    radioStyle: customStyle.radioStyle
                  },
                  error: hasError,
                  "data-testid": buttonId
                })), /*#__PURE__*/jsx(Label, {
                  htmlFor: buttonId,
                  labelStyle: labelStyle,
                  checked: isChecked,
                  disabled: disabled,
                  theme: theme,
                  children: direction === DIRECTION.COLUMN && note !== undefined ? /*#__PURE__*/jsxs(Stack, {
                    align: "flex-start",
                    gap: "0",
                    children: [label, /*#__PURE__*/jsx(Note, {
                      noteStyle: noteStyle,
                      children: note
                    })]
                  }) : label
                })]
              }, value)
            }, value);
          })
        }), /*#__PURE__*/jsx(Errors, {
          id: id,
          errors: errors,
          callouts: errorCallout
        })]
      })
    })
  });
};
process.env.NODE_ENV !== "production" ? RadioGroup.propTypes = {
  /** Specifies the alignment of radio buttons within the group. */
  alignment: PropTypes.oneOf([ALIGNMENT.CENTER, ALIGNMENT.END, ALIGNMENT.SPACE_AROUND, ALIGNMENT.SPACE_BETWEEN, ALIGNMENT.START]),
  /** Global [id](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id) attribute. */
  id: PropTypes.string,
  /** Form input [name](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#name) attribute. */
  name: PropTypes.string,
  /** Label passed to the fieldset [legend](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/legend) element. */
  legend: PropTypes.string,
  /** Array of selectable labels/values (label accepts `string` or any valid JSX element). */
  values: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.node,
    value: PropTypes.string,
    note: PropTypes.string
  })),
  /**
   * The value of the radio button (from the values property) that is initially selected.
   * NOTE: Use defaultSelected instead. In a future release, this prop will instead
   * control the controlled behaviour of the component (see `controlledSelected`).
   * */
  selected: PropTypes.string,
  /**
   * Alias for `selected`. NOTE: This will be the standard prop for specifying initial/default
   * values once `selected` behaviour is updated in v4.
   * */
  defaultSelected: PropTypes.string,
  /**
   * The value of the radio button (from the values property) that is currently selected.
   * NOTE: Currently the `selected` only specifies default behaviour, and can't actually control
   * the component. In v4, the old `selected` prop's behaviour will change to mirror
   * this prop, and this prop will be deprecated.
   * */
  controlledSelected: PropTypes.string,
  /** Specifies the direction of radio button group (row or column). */
  direction: PropTypes.oneOf([DIRECTION.ROW, DIRECTION.COLUMN]),
  /** Specifies the direction of radio button group on small screens (`<=576px`). */
  mobileDirection: PropTypes.oneOf([DIRECTION.ROW, DIRECTION.COLUMN]),
  /** WAI-ARIA [aria-label](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label) attribute. */
  ariaLabel: PropTypes.string,
  /** WAI-ARIA [aria-required](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-required) attribute. */
  ariaRequired: PropTypes.bool,
  /** HTML [disabled](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled) attribute. */
  disabled: PropTypes.bool,
  /** An array of error messages to be displayed. */
  errors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    msg: PropTypes.any,
    isError: PropTypes.bool
  }))]),
  /** Sets `aria-atomic=true`, `aria-live=polite` and `role=alert` on error messages (ensures errors are announced by screen readers as soon as they happen). */
  errorCallout: PropTypes.bool,
  /** Radio button [change](https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/change_event) event handler. */
  onChange: PropTypes.func,
  /** Style overrides. */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object,
    legendStyle: PropTypes.object,
    radioStyle: PropTypes.object,
    labelStyle: PropTypes.object,
    noteStyle: PropTypes.object
  }),
  /**
   * [Tooltip](/docs/tooltip) to be displayed beside the label.
   */
  tooltip: componentWithName('Tooltip'),
  /** CSS [gap](https://developer.mozilla.org/en-US/docs/Web/CSS/gap) property for spacing between radio buttons. */
  gap: PropTypes.string,
  /** CSS [column-gap](https://developer.mozilla.org/en-US/docs/Web/CSS/column-gap)
   * property for horizontal spacing between radio buttons.
   */
  gapX: PropTypes.string,
  /** CSS [row-gap](https://developer.mozilla.org/en-US/docs/Web/CSS/row-gap)
   * property for vertical spacing between radio buttons.
   */
  gapY: PropTypes.string,
  /** CSS [flex-wrap](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap) property for the container. */
  wrap: PropTypes.string
} : undefined;
process.env.NODE_ENV !== "production" ? RadioGroup.displayName = 'RadioGroup' : undefined;

export { RadioGroup as default };
